import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { 
  MdArrowBack, 
  MdLock,
  MdVisibility,
  MdVisibilityOff,
  MdCheckCircle,
  MdError,
  MdCheck,
  MdClose
} from 'react-icons/md';

/**
 * Change Password Component
 * Allows portal users (tutors) to update their password
 */
function ChangePassword() {
  const navigate = useNavigate();
  const { getToken, user } = useAttendanceAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const [validationErrors, setValidationErrors] = useState({});
  
  // Password requirements checker
  const checkPasswordRequirements = (password) => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /(?=.*[A-Z])/.test(password),
      hasLowercase: /(?=.*[a-z])/.test(password),
      hasNumber: /(?=.*\d)/.test(password),
      hasSpecialChar: /(?=.*[@$!%*?&])/.test(password)
    };
  };
  
  const passwordRequirements = checkPasswordRequirements(formData.new_password);

  const validateForm = () => {
    const errors = {};

    // Trim values for validation
    const currentPassword = formData.current_password?.trim() || '';
    const newPassword = formData.new_password?.trim() || '';
    const confirmPassword = formData.confirm_password?.trim() || '';

    if (!currentPassword) {
      errors.current_password = 'Current password is required';
    }

    if (!newPassword) {
      errors.new_password = 'New password is required';
    } else if (newPassword.length < 8) {
      errors.new_password = 'New password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])/.test(newPassword)) {
      errors.new_password = 'New password must contain at least one lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(newPassword)) {
      errors.new_password = 'New password must contain at least one uppercase letter';
    } else if (!/(?=.*\d)/.test(newPassword)) {
      errors.new_password = 'New password must contain at least one number';
    } else if (!/(?=.*[@$!%*?&])/.test(newPassword)) {
      errors.new_password = 'New password must contain at least one special character (@$!%*?&)';
    }

    if (!confirmPassword) {
      errors.confirm_password = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      errors.confirm_password = 'Passwords do not match';
    }

    if (currentPassword && newPassword && currentPassword === newPassword) {
      errors.new_password = 'New password must be different from current password';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear success message when form changes
    if (success) {
      setSuccess(false);
    }
    if (error) {
      setError(null);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted!');
    console.log('Form data:', {
      current_password: formData.current_password ? '***' + formData.current_password.slice(-2) : 'empty',
      new_password: formData.new_password ? '***' + formData.new_password.slice(-2) : 'empty',
      confirm_password: formData.confirm_password ? '***' + formData.confirm_password.slice(-2) : 'empty',
      current_password_length: formData.current_password?.length || 0,
      new_password_length: formData.new_password?.length || 0,
      confirm_password_length: formData.confirm_password?.length || 0
    });
    
    setError(null);
    setSuccess(false);

    console.log('Validating form...');
    if (!validateForm()) {
      console.log('Form validation failed');
      console.log('Validation errors:', validationErrors);
      return;
    }
    console.log('Form validation passed!');

    try {
      setLoading(true);
      const token = getToken();
      console.log('Token exists:', !!token);

      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // Don't trim passwords - send them exactly as entered
      // Trimming could remove valid password characters or cause mismatches
      const currentPassword = formData.current_password || '';
      const newPassword = formData.new_password || '';

      console.log('Password data prepared:', {
        currentPasswordLength: currentPassword.length,
        currentPasswordPreview: currentPassword ? currentPassword.substring(0, 1) + '***' + currentPassword.slice(-1) : 'EMPTY',
        newPasswordLength: newPassword.length,
        newPasswordPreview: newPassword ? newPassword.substring(0, 1) + '***' + newPassword.slice(-1) : 'EMPTY',
        note: 'Passwords sent exactly as entered (no trimming)'
      });

      // Double-check that passwords are not empty (shouldn't happen if validation passed, but safety check)
      if (!currentPassword || !newPassword) {
        console.error('Passwords are empty after trimming!');
        setError('Current password and new password are required');
        setLoading(false);
        return;
      }

      console.log('Sending password change request:', {
        hasCurrentPassword: !!currentPassword,
        currentPasswordLength: currentPassword.length,
        currentPasswordValue: currentPassword ? '***' + currentPassword.slice(-2) : 'empty',
        hasNewPassword: !!newPassword,
        newPasswordLength: newPassword.length,
        newPasswordValue: newPassword ? '***' + newPassword.slice(-2) : 'empty',
        formData: {
          current_password_length: formData.current_password?.length || 0,
          new_password_length: formData.new_password?.length || 0
        }
      });

      const passwordData = {
        current_password: currentPassword,
        new_password: newPassword
      };
      
      console.log('Calling updatePassword with:', {
        passwordDataKeys: Object.keys(passwordData),
        current_password_exists: 'current_password' in passwordData,
        new_password_exists: 'new_password' in passwordData,
        current_password_length: passwordData.current_password?.length || 0,
        new_password_length: passwordData.new_password?.length || 0,
        current_password_value: passwordData.current_password ? '***' + passwordData.current_password.slice(-2) : 'EMPTY',
        new_password_value: passwordData.new_password ? '***' + passwordData.new_password.slice(-2) : 'EMPTY',
        passwordDataStringified: JSON.stringify(passwordData)
      });

      const result = await attendanceAPI.updatePassword(
        passwordData,
        token
      );

      console.log('Password change API response:', result);

      if (result.success) {
        setSuccess(true);
        // Reset form
        setFormData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        setValidationErrors({});
        
        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/attendance/dashboard');
        }, 2000);
      } else {
        // Show more specific error messages
        let errorMessage = result.message || 'Failed to update password';
        
        // Check for specific error types and provide helpful messages
        if (result.error === 'InvalidPassword' || errorMessage.toLowerCase().includes('incorrect')) {
          errorMessage = (
            'The current password you entered is incorrect. ' +
            'Please make sure you are using the exact password you use to log in. ' +
            'Passwords are case-sensitive and must match exactly. ' +
            'If you are unsure, try logging out and logging back in to verify your password.'
          );
        } else if (result.error === 'WeakPassword' || errorMessage.toLowerCase().includes('requirements')) {
          errorMessage = (
            'The new password does not meet the security requirements. ' +
            'Please ensure it has at least 8 characters, including uppercase, lowercase, number, and special character (@$!%*?&).'
          );
        } else if (result.error === 'SamePassword') {
          errorMessage = 'The new password must be different from your current password.';
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Error updating password:', err);
      let errorMessage = err.message || 'Failed to update password. Please try again.';
      
      // Check for specific error types in the error message
      if (errorMessage.toLowerCase().includes('incorrect') || errorMessage.toLowerCase().includes('invalid')) {
        errorMessage = (
          'The current password you entered is incorrect. ' +
          'Please verify you are using the exact password you use to log in. ' +
          'If you are unsure, try logging out and logging back in to confirm your password.'
        );
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/attendance/dashboard');
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition"
        >
          <MdArrowBack className="mr-2" />
          <span>Back to Dashboard</span>
        </button>
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-blue-100 rounded-lg">
            <MdLock className="text-2xl text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Change Password</h1>
            <p className="text-gray-600 mt-1">Update your portal account password</p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3">
          <MdCheckCircle className="text-2xl text-green-600 flex-shrink-0" />
          <div>
            <p className="text-green-800 font-medium">Password updated successfully!</p>
            <p className="text-green-600 text-sm mt-1">Redirecting to dashboard...</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
          <MdError className="text-2xl text-red-600 flex-shrink-0" />
          <div>
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Password */}
          <div>
            <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-2">
              Current Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPasswords.current ? 'text' : 'password'}
                id="current_password"
                name="current_password"
                value={formData.current_password}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.current_password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your current password"
                disabled={loading || success}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPasswords.current ? (
                  <MdVisibilityOff className="text-xl" />
                ) : (
                  <MdVisibility className="text-xl" />
                )}
              </button>
            </div>
            {validationErrors.current_password && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.current_password}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-2">
              New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPasswords.new ? 'text' : 'password'}
                id="new_password"
                name="new_password"
                value={formData.new_password}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.new_password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter your new password"
                disabled={loading || success}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPasswords.new ? (
                  <MdVisibilityOff className="text-xl" />
                ) : (
                  <MdVisibility className="text-xl" />
                )}
              </button>
            </div>
            {validationErrors.new_password && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.new_password}</p>
            )}
            {/* Real-time Password Requirements */}
            {formData.new_password ? (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs font-medium text-gray-700 mb-2">Password Requirements:</p>
                <ul className="space-y-1.5">
                  <li className={`flex items-center text-xs ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                    {passwordRequirements.minLength ? (
                      <MdCheck className="mr-2 text-green-600 flex-shrink-0" />
                    ) : (
                      <MdClose className="mr-2 text-gray-400 flex-shrink-0" />
                    )}
                    <span>At least 8 characters {passwordRequirements.minLength ? '' : `(${formData.new_password.length}/8)`}</span>
                  </li>
                  <li className={`flex items-center text-xs ${passwordRequirements.hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
                    {passwordRequirements.hasUppercase ? (
                      <MdCheck className="mr-2 text-green-600 flex-shrink-0" />
                    ) : (
                      <MdClose className="mr-2 text-gray-400 flex-shrink-0" />
                    )}
                    <span>One uppercase letter (A-Z)</span>
                  </li>
                  <li className={`flex items-center text-xs ${passwordRequirements.hasLowercase ? 'text-green-600' : 'text-gray-500'}`}>
                    {passwordRequirements.hasLowercase ? (
                      <MdCheck className="mr-2 text-green-600 flex-shrink-0" />
                    ) : (
                      <MdClose className="mr-2 text-gray-400 flex-shrink-0" />
                    )}
                    <span>One lowercase letter (a-z)</span>
                  </li>
                  <li className={`flex items-center text-xs ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                    {passwordRequirements.hasNumber ? (
                      <MdCheck className="mr-2 text-green-600 flex-shrink-0" />
                    ) : (
                      <MdClose className="mr-2 text-gray-400 flex-shrink-0" />
                    )}
                    <span>One number (0-9)</span>
                  </li>
                  <li className={`flex items-center text-xs ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`}>
                    {passwordRequirements.hasSpecialChar ? (
                      <MdCheck className="mr-2 text-green-600 flex-shrink-0" />
                    ) : (
                      <MdClose className="mr-2 text-gray-400 flex-shrink-0" />
                    )}
                    <span>One special character (@$!%*?&)</span>
                  </li>
                </ul>
                {/* Overall status */}
                <div className="mt-2 pt-2 border-t border-gray-200">
                  {Object.values(passwordRequirements).every(req => req) ? (
                    <p className="text-xs text-green-600 font-medium flex items-center">
                      <MdCheckCircle className="mr-1" />
                      All requirements met!
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500">
                      {Object.values(passwordRequirements).filter(req => req).length} of 5 requirements met
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-2 text-xs text-gray-500">
                <p>Password must contain:</p>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  <li>At least 8 characters</li>
                  <li>One uppercase letter</li>
                  <li>One lowercase letter</li>
                  <li>One number</li>
                  <li>One special character (@$!%*?&)</li>
                </ul>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm New Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirm_password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  validationErrors.confirm_password ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Confirm your new password"
                disabled={loading || success}
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPasswords.confirm ? (
                  <MdVisibilityOff className="text-xl" />
                ) : (
                  <MdVisibility className="text-xl" />
                )}
              </button>
            </div>
            {validationErrors.confirm_password && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.confirm_password}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={handleBack}
              className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || success}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              disabled={loading || success}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <MdLock className="text-lg" />
                  <span>Update Password</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* User Info */}
      {user && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-600">
            <span className="font-medium">Account:</span> {user.name || user.email || 'Portal User'}
          </p>
          {user.email && (
            <p className="text-sm text-gray-600 mt-1">
              <span className="font-medium">Email:</span> {user.email}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ChangePassword;
